import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface ServiceWorkerContextType {
  isUpdateAvailable: boolean;
  registration: ServiceWorkerRegistration | null;
  updateApp: () => void;
}

const ServiceWorkerContext = createContext<ServiceWorkerContextType>({
  isUpdateAvailable: false,
  registration: null,
  updateApp: () => {},
});

export const useServiceWorker = () => useContext(ServiceWorkerContext);

interface ServiceWorkerProviderProps {
  children: React.ReactNode;
}

export const ServiceWorkerProvider: React.FC<ServiceWorkerProviderProps> = ({ children }) => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  // Called when a new service worker has installed and is waiting
  const onUpdate = useCallback((reg: ServiceWorkerRegistration) => {
    setRegistration(reg);
    setWaitingWorker(reg.waiting);
    setIsUpdateAvailable(true);
  }, []);

  // Called when content is first cached for offline use
  const onSuccess = useCallback((reg: ServiceWorkerRegistration) => {
    setRegistration(reg);
    console.log('PWA content cached for offline use');
  }, []);

  // Apply the update by telling the service worker that is waiting NOW to skip waiting.
  // Read it at click time: the 60-second update check can install a newer worker after the
  // banner appeared, and a message to the older (now redundant) one is silently ignored.
  const updateApp = useCallback(async () => {
    const reg = await navigator.serviceWorker?.getRegistration();
    const waiting = reg?.waiting ?? waitingWorker;
    if (!waiting) return;
    waiting.postMessage({ type: 'SKIP_WAITING' });
    setIsUpdateAvailable(false);
  }, [waitingWorker]);

  useEffect(() => {
    // Register service worker with callbacks
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
      if (publicUrl.origin !== window.location.origin) {
        return;
      }

      const registerSW = async () => {
        try {
          const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
          const reg = await navigator.serviceWorker.register(swUrl);

          // Installed apps on phones are usually resumed rather than relaunched, and a
          // backgrounded page's timers are paused, so the 60-second check below does not run
          // there. Check for a new version whenever the app comes back to the foreground.
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') reg.update().catch(() => undefined);
          });

          // Check if there's already a waiting worker
          if (reg.waiting) {
            setWaitingWorker(reg.waiting);
            setIsUpdateAvailable(true);
            setRegistration(reg);
          }

          // Listen for new service workers installing
          reg.onupdatefound = () => {
            const installingWorker = reg.installing;
            if (!installingWorker) return;

            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // New content available
                  onUpdate(reg);
                } else {
                  // Content cached for offline use
                  onSuccess(reg);
                }
              }
            };
          };

          // Periodically check for updates (every 60 seconds)
          const checkForUpdates = () => {
            reg.update().catch(console.error);
          };
          const intervalId = setInterval(checkForUpdates, 60 * 1000);

          return () => clearInterval(intervalId);
        } catch (error) {
          console.error('Service worker registration failed:', error);
        }
      };

      // Register after page loads
      if (document.readyState === 'complete') {
        registerSW();
      } else {
        window.addEventListener('load', registerSW);
        return () => window.removeEventListener('load', registerSW);
      }
    }
  }, [onUpdate, onSuccess]);

  useEffect(() => {
    // When the service worker controlling this page changes (after skipWaiting),
    // reload the page to get the new content
    if ('serviceWorker' in navigator) {
      let refreshing = false;

      const handleControllerChange = () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      };

      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

      return () => {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      };
    }
  }, []);

  return (
    <ServiceWorkerContext.Provider value={{ isUpdateAvailable, registration, updateApp }}>
      {children}
    </ServiceWorkerContext.Provider>
  );
};
